import {customElement, property} from "lit/decorators.js";
import {html, LitElement, type TemplateResult} from "lit";
import Styles from "./stockchart.lit.scss";
import {EventBus} from "../../common/eventBus";
import {resolve} from "../../container";
import {COLOR_SCHEME_CHANGED_EVENT} from "../../common/colorSchemeService";
import type {Maybe} from "../../bootstrap/common/types";

const STOCK_CHART_COLORS = [
    ["BGCOLOR", "--background-default"],
    ["BOXCOLOR", "--background-default"],
    ["INPUTTEXTCOLOR", "--text-color"],
    ["BOXHEADERTEXTCOLOR", "--text-color"],
    ["BOXTITLECOLOR", "--text-color"],
    ["BOXTEXTCOLOR", "--text-color"],
    ["AXISLABELCOLOR", "--text-color"],
    ["BRANDTEXTCOLOR", "--text-color"],
    ["BRANDDETAILSCOLOR", "--text-color"],
    ["BRANDVALUECOLOR", "--company-value-color"],
    ["BTNCOLOR", "--primary-button-color"],
    ["BTNTEXTCOLOR", "--primary-button-text-color"],
    ["BTNHOVERCOLOR", "--primary-button-hover-color"],
    ["BTNHOVERTEXTCOLOR", "--primary-button-hover-text-color"],
    ["POSITIVEVALUECOLOR", "--positive-value-color"],
    ["NEGATIVEVALUECOLOR", "--negative-value-color"],
    ["COMPARELINECOLOR", "--stockchart-color-1"],
    ["SECONDCOMPARELINECOLOR", "--stockchart-color-2"],
    ["THIRDCOMPARELINECOLOR", "--stockchart-color-3"],
    ["CHARTCOLOR", "--chart-area-color"],
    ["LINECOLOR", "--chart-line-color"],
    ["TOOLTIPCOLOR", "--tooltip-box-color"],
    ["TOOLTIPTEXTCOLOR", "--text-color"],
    ["CHARTGRIDCOLOR", "--chart-area-color"],
    ["INPUTBGCOLOR", "--input-background-color"],
    ["INPUTFOCUSBGCOLOR", "--input-background-color"],
    ["INPUTFOCUSTEXTCOLOR", "--text-color"]
];

const DEUTSCHE_BOERSE_API = `https://xms.deutsche-boerse.com/api/v1/chartmodule`;
const ENBW_ISIN = "DE0005220008";

const HEX_COLOR_PATTERN = /^#((\d|[a-fA-F]){3}|(\d|[a-fA-F]){6})$/;

@customElement("eop-stockchart")
export class EopStockchart extends LitElement {

    public static readonly styles = Styles;

    @property({attribute: "lang"})
    public lang: string = "de";
    @property({attribute: "title"})
    public title: string;

    public constructor(
        private eventBus: EventBus = resolve(EventBus)
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.eventBus.on(COLOR_SCHEME_CHANGED_EVENT, () => this.requestUpdate());
    }

    public render(): TemplateResult {
        return html`${this.iframeElement()}`;
    }

    private iframeElement(): HTMLIFrameElement {
        const iframe = document.createElement("iframe");
        iframe.title = this.title;
        iframe.classList.add("stock-chart");
        iframe.src = this.buildDeutscheBoerseUrl(this.lang);
        iframe.setAttribute("scrolling", "no");
        return iframe;
    }

    private buildDeutscheBoerseUrl(lang: string): string {
        return `${DEUTSCHE_BOERSE_API}?ISIN=${ENBW_ISIN}&LANG=${lang}${this.colorsParams()}`;
    }

    private colorsParams(): string {
        return STOCK_CHART_COLORS
            .slice(0, 27) // Ensure we avoid a "too long"-Url problem
            .reduce((acc, [key, value]) => {
                const cssVar = this.resolveCssVariable(value);
                if (!cssVar) {
                    return acc;
                }
                return `${acc}&${key}=${this.getHexValue(cssVar)}`;
            }, "");
    }

    private resolveCssVariable(cssVariable: string): Maybe<string> {
        return getComputedStyle(this).getPropertyValue(cssVariable);
    }

    private getHexValue(str: string): string {
        return this.isHexColor(str) ? str.slice(1) : str;
    }

    private isHexColor(str: string): boolean {
        return HEX_COLOR_PATTERN.test(this.trim(str));
    }

    private trim(str: string): string {
        return str.replaceAll(/\s/g, "");
    }
}

